import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, BarChart3, RefreshCw, MapPin, Calendar, ChevronRight } from 'lucide-react';
import WidgetCard from './WidgetCard';
import { useDashboardStore } from '../../../store/useDashboardStore';
import { ValuationData } from '../../../types/valuation';

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${Math.round(value / 1000).toLocaleString()}K`;
  }
  return `$${Math.round(value).toLocaleString()}`;
}

function calculateEstimatedValue(data: ValuationData): number {
  if (data.apiResponse?.result?.total_station_value) {
    return data.apiResponse.result.total_station_value;
  }
  const fuelRevenue = (data.annualGallons || 0) * (data.fuelMargin || 0);
  const storeRevenue = (data.storeRevenue || 0) * 12;
  const expenses = (data.operatingExpenses || 0) * 12;
  const netIncome = fuelRevenue + storeRevenue - expenses;
  return netIncome * 3.5 || 0;
}

export default function MyValuationsWidget() {
  const navigate = useNavigate();
  const { valuations, isLoadingValuations, valuationsError } = useDashboardStore();

  // Show most recent 5
  const recentValuations = [...valuations]
    .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime())
    .slice(0, 5);

  return (
    <WidgetCard
      title="My Valuations"
      icon={<FileText className="w-5 h-5" />}
      colSpan={2}
      isLoading={isLoadingValuations}
      error={valuationsError}
      action={
        valuations.length > 5 ? (
          <Link
            to="/dashboard/valuations"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View All ({valuations.length})
          </Link>
        ) : null
      }
    >
      {recentValuations.length === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No valuations yet.</p>
          <button
            onClick={() => navigate('/valuation')}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Run your first valuation
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {recentValuations.map((valuation) => {
            const estimatedValue = calculateEstimatedValue(valuation.data);
            return (
              <div
                key={valuation.id}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-colors cursor-pointer group"
                onClick={() =>
                  navigate(`/dashboard/performance-analysis/${valuation.id}`)
                }
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {valuation.data.location || 'Unnamed Property'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-3 h-3 text-gray-300" />
                    <p className="text-xs text-gray-500">
                      {new Date(valuation.lastModified).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-blue-600">
                    {estimatedValue > 0 ? formatCurrency(estimatedValue) : '—'}
                  </span>

                  {/* Re-run button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/valuation', {
                        state: { prefillData: valuation.data, rerunMode: true },
                      });
                    }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
                    title="Re-run with different inputs"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>

                  {/* View details */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/performance-analysis/${valuation.id}`);
                    }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
                    title="View details & analysis"
                  >
                    <BarChart3 className="w-3.5 h-3.5" />
                  </button>

                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}
